import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  TextField,
  Tab,
  Autocomplete,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined'
import { LoadingButton } from '@mui/lab'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import TestListCycleIcon from '../../assets/iconComponents/TestListCycleIcon'
import PageService from "../../services/PageService";


const AddTestsModal = ({
  elInfo,
  testCases,
  addTestsOpen,
  setAddTestsOpen,
  setTestCasesToggler
}) => {
  const formatTestCaseLabel = (tc) => {
    if (!tc) return '';
    const id = tc.publicId || tc.public_id;
    const name = tc.name || '';
    return id ? `${id} — ${name}` : name;
  };

  const testCaseMatchesSearch = (testCase, term) => {
    if (!testCase) return false;
    const query = (term || '').toLowerCase();
    if (!query) return true;
    return (
      (testCase.name || '').toLowerCase().includes(query) ||
      (testCase.publicId || testCase.public_id || '').toLowerCase().includes(query)
    );
  };

  const getFolderTestCases = (folder) =>
    (Array.isArray(folder?.testCases) && folder.testCases) ||
    (Array.isArray(folder?.test_cases) && folder.test_cases) ||
    [];

  const [activeTabValue, setActiveTabValue] = useState('1')
  const [loading, setLoading] = useState(false)
  const [selectedTestCases, setSelectedTestCases] = useState([])
  const [searchText, setSearchText] = useState('')
  const [singleSelectValues, setSingleSelectValues] = useState([])
  const [selectedVersionId, setSelectedVersionId] = useState(null)
  const [selectedCycleId, setSelectedCycleId] = useState(null)
  const [selectedFolderId, setSelectedFolderId] = useState(null)
  const generalVersions = testCases.filter((version) => version.type === 'general')
  const generalTestCasesOptions = generalVersions.flatMap((version) =>
    version.cycles.flatMap((cycle) =>
      cycle.folders.flatMap((folder) => getFolderTestCases(folder))
    )
  )
  const customVersions = testCases.filter((version) => version.type === 'custom')
  const allTestCaseOptions = useMemo(() => {
    const seen = new Set()
    const list = []
    testCases.forEach((version) => {
      version.cycles?.forEach((cycle) => {
        cycle.folders?.forEach((folder) => {
          getFolderTestCases(folder).forEach((tc) => {
            if (!tc?.id) return
            if (seen.has(tc.id)) return
            seen.add(tc.id)
            list.push(tc)
          })
        })
      })
    })
    return list
  }, [testCases])
  const selectedTestCaseDetails = useMemo(() => {
    if (!selectedTestCases.length) return []
    const lookup = new Map(allTestCaseOptions.map((tc) => [tc.id, tc]))
    return selectedTestCases
      .map((id) => lookup.get(id))
      .filter(Boolean)
  }, [allTestCaseOptions, selectedTestCases])
  const maxSelectedPreview = 8
  const previewSelectedTestCases = selectedTestCaseDetails.slice(0, maxSelectedPreview)
  const hiddenSelectedCount = Math.max(
    selectedTestCaseDetails.length - maxSelectedPreview,
    0
  )


  useEffect(() => {
    if (!addTestsOpen) {
      setSelectedTestCases([])
      setSearchText('')
      setSingleSelectValues([])
      setSelectedVersionId(null)
      setSelectedCycleId(null)
      setSelectedFolderId(null)
      setActiveTabValue('1')
    }
  }, [addTestsOpen])

  const handleCloseModal = () => {
    setAddTestsOpen(false)
  }

  const handleTabChange = (event, newValue) => {
    setActiveTabValue(newValue)
    setSelectedTestCases([])
    setSingleSelectValues([])
    setSelectedVersionId(null)
    setSelectedCycleId(null)
    setSelectedFolderId(null)
  }

  const handleSearchInputChange = (event, newInputValue) => {
    setSearchText(newInputValue || '')
  }

  const getAllTestCaseIdsInFolder = (folder) =>
    getFolderTestCases(folder).map((tc) => tc.id)

  const handleCheckboxChange = (id, type, folder = null) => {
    setSelectedTestCases((prevSelected) => {
      let newSelected = [...prevSelected]

      if (type === 'testCase') {
        if (newSelected.includes(id)) {
          newSelected = newSelected.filter((tcId) => tcId !== id)
        } else {
          newSelected.push(id)
        }
      } else if (type === 'folder' && folder) {
        const folderTestCaseIds = getAllTestCaseIdsInFolder(folder)
        const allSelectedInFolder = folderTestCaseIds.every(tcId => newSelected.includes(tcId))

        if (allSelectedInFolder) {
          newSelected = newSelected.filter(tcId => !folderTestCaseIds.includes(tcId))
        } else {
          folderTestCaseIds.forEach(tcId => {
            if (!newSelected.includes(tcId)) {
              newSelected.push(tcId)
            }
          })
        }
      }
      return newSelected
    })
  }

  const isTestCaseSelected = (testCaseId) => selectedTestCases.includes(testCaseId)

  const isFolderSelected = (folder) => {
    const folderCases = getFolderTestCases(folder)
    if (!folder || !folderCases.length) return false
    return folderCases.every(tc => selectedTestCases.includes(tc.id))
  }

  const isFolderIndeterminate = (folder) => {
    const folderCases = getFolderTestCases(folder)
    if (!folder || !folderCases.length) return false
    const selectedCount = folderCases.filter(tc => selectedTestCases.includes(tc.id)).length
    return selectedCount > 0 && selectedCount < folderCases.length
  }

  const getAllTestCaseIdsInVersion = (version) => {
    const ids = []
    version.cycles?.forEach((cycle) => {
      if (cycle.type === 'general') {
        cycle.folders?.forEach((folder) => {
          getFolderTestCases(folder).forEach((tc) => {
            if (tc?.id) ids.push(tc.id)
          })
        })
      }
    })
    return ids
  }

  const isVersionSelected = (version) => {
    const allIds = getAllTestCaseIdsInVersion(version)
    if (!allIds.length) return false
    return allIds.every(id => selectedTestCases.includes(id))
  }

  const isVersionIndeterminate = (version) => {
    const allIds = getAllTestCaseIdsInVersion(version)
    if (!allIds.length) return false
    const selectedCount = allIds.filter(id => selectedTestCases.includes(id)).length
    return selectedCount > 0 && selectedCount < allIds.length
  }

  const handleVersionCheckboxChange = (version) => {
    const allIds = getAllTestCaseIdsInVersion(version)
    setSelectedTestCases((prevSelected) => {
      const allAlreadySelected = allIds.every(id => prevSelected.includes(id))
      if (allAlreadySelected) {
        return prevSelected.filter(id => !allIds.includes(id))
      } else {
        const newSelected = [...prevSelected]
        allIds.forEach(id => {
          if (!newSelected.includes(id)) {
            newSelected.push(id)
          }
        })
        return newSelected
      }
    })
  }

  const handleAddTests = async () => {
    try {
      setLoading(true)
      const cycleId = elInfo?.cycleId || elInfo?.id
      if (!cycleId) {
        return
      }
      if (!selectedTestCases.length) {
        return
      }
      await PageService.addTestsToCycle(cycleId, {
        testCaseIds: selectedTestCases,
      })
      setTestCasesToggler(prev => !prev)
      setAddTestsOpen(false)
      setLoading(false)
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const TreeListDataRow = ({ level = 0, children, onClick = null, isActiveFolder = false }) => (
    <Box
      className="treeListDataRow"
      onClick={onClick}
      sx={{
        pl: `${level * 10}px`,
        cursor: onClick ? 'pointer' : 'default',
        backgroundColor: isActiveFolder ? 'rgba(69, 116, 210, 0.1)' : 'inherit',
      }}
    >
      {children}
    </Box>
  )

  const renderTestCase = (tc) => (
    <TreeListDataRow key={tc.id} level={11.3}>
      <Box className="treeListDataTestCaseTitle">
        <FormControlLabel
          control={
            <Checkbox
              checked={isTestCaseSelected(tc.id)}
              onChange={() => handleCheckboxChange(tc.id, 'testCase')}
            />
          }
          label={
            <Box className="treeListDataTestCaseTitleContent">
              <ListAltOutlinedIcon className="treeListDataTestCaseTitleContentIcon" />
              <Typography>{formatTestCaseLabel(tc)}</Typography>
            </Box>
          }
        />
      </Box>
    </TreeListDataRow>
  )

  const renderFolder = (folder) => (
    <Accordion
      key={folder.id}
    >
      <AccordionSummary
        className="treeListDataFolderTitle"
        expandIcon={<ExpandMoreIcon color='primary' />}
      >
        <TreeListDataRow level={8}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isFolderSelected(folder)}
                indeterminate={isFolderIndeterminate(folder)}
                onChange={() => handleCheckboxChange(folder.id, 'folder', folder)}
                onClick={(event) => event.stopPropagation()}
              />
            }
            label={
              <Box className="treeListDataFolderTitleContent">
                <FolderOutlinedIcon className="treeListDataFolderTitleContentIcon" />
                <Typography>{folder.name}</Typography>
              </Box>
            }
          />
        </TreeListDataRow>
      </AccordionSummary>
      <AccordionDetails>
        {getFolderTestCases(folder).map(renderTestCase)}
      </AccordionDetails>
    </Accordion>
  )

  const renderTreeList = () => {
    let dataToRender = generalVersions

    if (searchText) {
      const filteredVersions = dataToRender.map(version => {
        const filteredCycles = version.cycles.map(cycle => {
          const filteredFolders = cycle.folders.map(folder => {
            const filteredTestCasesInFolder = getFolderTestCases(folder).filter(testCase =>
              testCaseMatchesSearch(testCase, searchText)
            )
            return { ...folder, testCases: filteredTestCasesInFolder }
          }).filter(folder => folder.testCases.length > 0)
          return { ...cycle, folders: filteredFolders }
        }).filter(cycle => cycle.folders.length > 0)
        return { ...version, cycles: filteredCycles }
      }).filter(version => version.cycles.length > 0)
      dataToRender = filteredVersions
    }

    return (
      <Box>
        {dataToRender.map((version) => (
          <Accordion
            key={version.id}
            className='treeListDataContainer'
            defaultExpanded
          >
            <AccordionSummary
              className="treeListDataFolderTitle"
              expandIcon={<ExpandMoreIcon color='primary' />}
            >
              <TreeListDataRow level={5}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isVersionSelected(version)}
                      indeterminate={isVersionIndeterminate(version)}
                      onChange={() => handleVersionCheckboxChange(version)}
                      onClick={(event) => event.stopPropagation()}
                    />
                  }
                  label={
                    <Box className="treeListDataFolderTitleContent">
                      <TestListCycleIcon className="treeListDataFolderTitleContentIcon" />
                      <Typography sx={{ fontWeight: 700 }}>{version.name}</Typography>
                    </Box>
                  }
                />
              </TreeListDataRow>
            </AccordionSummary>
            <AccordionDetails className="treeListDataVersionContent">
              {version.cycles.map((cycle) => (
                cycle.type === 'general' && cycle.folders.map(renderFolder)
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
        {dataToRender.length === 0 && searchText && (
          <Typography sx={{ mt: 2, ml: 2, color: 'text.secondary' }}>No matching test cases found.</Typography>
        )}
      </Box>
    )
  }

  return (
    <Dialog open={addTestsOpen} onClose={handleCloseModal}>
      <Box className="addTestCaseModal">
        <DialogTitle className="addTestCaseModalTitle">
          Add Tests
        </DialogTitle>
        <DialogContent className="addTestCaseModalContent">
          <TabContext value={activeTabValue}>
            <TabList className="addTestCaseModalTabsToggler" onChange={handleTabChange}>
              <Tab
                className="addTestCaseModalTabLabelContainer"
                label={
                  <Box
                    className="addTestCaseModalTabLabel"
                    sx={{
                      color: activeTabValue === "1" ? "#4574D2" : "rgba(0, 0, 0, 0.6)"
                    }}
                  >
                    <span className="addTestCaseModalTabLabelText">Individually</span>
                  </Box>
                }
                value="1"
              />
              <Tab
                className="addTestCaseModalTabLabelContainer"
                label={
                  <Box
                    className="addTestCaseModalTabLabel"
                    sx={{
                      color: activeTabValue === "2" ? "#4574D2" : "rgba(0, 0, 0, 0.6)"
                    }}
                  >
                    <span className="addTestCaseModalTabLabelText">From Another Cycle</span>
                  </Box>
                }
                value="2"
              />
              <Tab
                className="addTestCaseModalTabLabelContainer"
                label={
                  <Box
                    className="addTestCaseModalTabLabel"
                    sx={{
                      color: activeTabValue === "3" ? "#4574D2" : "rgba(0, 0, 0, 0.6)"
                    }}
                  >
                    <span className="addTestCaseModalTabLabelText">Tree Selection</span>
                  </Box>
                }
                value="3"
              />
            </TabList>
            <TabPanel value="1" className='addTestCaseModalIndividuallyTab'>
              <Typography variant='body1' mb={2}>
                Start typing to search test cases to add to this cycle.
              </Typography>
              <Autocomplete
                multiple
                fullWidth
                value={singleSelectValues}
                options={allTestCaseOptions}
                getOptionLabel={(option) => formatTestCaseLabel(option)}
                filterOptions={(options, { inputValue }) =>
                  options.filter((option) =>
                    testCaseMatchesSearch(option, inputValue)
                  )
                }
                onChange={(_, value) => {
                  setSingleSelectValues(value)
                  setSelectedTestCases(value.map((tc) => tc.id).filter(Boolean))
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search Test Case..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#2D4356' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {formatTestCaseLabel(option)}
                  </li>
                )}
              />
            </TabPanel>
            <TabPanel value="2" className='addTestCaseModalFromCycleTab'>
              <Typography variant='body1' mb={3}>
                Select version, cycle, and folder to copy all test cases.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Autocomplete
                  fullWidth
                  options={customVersions}
                  getOptionLabel={(option) => option?.name || ''}
                  value={customVersions.find((v) => v.id === selectedVersionId) || null}
                  onChange={(_, value) => {
                    setSelectedVersionId(value?.id || null)
                    setSelectedCycleId(null)
                    setSelectedFolderId(null)
                    setSelectedTestCases([])
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Versions" variant="outlined" />
                  )}
                />
                <Autocomplete
                  fullWidth
                  options={
                    customVersions
                      .find((v) => v.id === selectedVersionId)
                      ?.cycles || []
                  }
                  getOptionLabel={(option) => option?.name || ''}
                  value={
                    customVersions
                      .find((v) => v.id === selectedVersionId)
                      ?.cycles.find((c) => c.id === selectedCycleId) || null
                  }
                  onChange={(_, value) => {
                    setSelectedCycleId(value?.id || null)
                    setSelectedFolderId(null)
                    const ids =
                      value?.folders?.flatMap((f) =>
                        f.testCases?.map((tc) => tc.id).filter(Boolean) || []
                      ) || []
                    setSelectedTestCases(ids)
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Test Cycle" variant="outlined" />
                  )}
                  disabled={!selectedVersionId}
                />
                <Autocomplete
                  fullWidth
                  options={
                    customVersions
                      .find((v) => v.id === selectedVersionId)
                      ?.cycles.find((c) => c.id === selectedCycleId)
                      ?.folders || []
                  }
                  getOptionLabel={(option) => option?.name || ''}
                  value={
                    customVersions
                      .find((v) => v.id === selectedVersionId)
                      ?.cycles.find((c) => c.id === selectedCycleId)
                      ?.folders.find((f) => f.id === selectedFolderId) || null
                  }
                  onChange={(_, value) => {
                    setSelectedFolderId(value?.id || null)
                    const ids =
                      value?.testCases
                        ?.map((tc) => tc.id)
                        .filter(Boolean) || []
                    setSelectedTestCases(ids)
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Test Folder" variant="outlined" />
                  )}
                  disabled={!selectedCycleId}
                />
              </Box>
            </TabPanel>
            <TabPanel value="3" className='addTestCaseModalTreeSelectionTab'>
              <Typography variant='body1' mb={3}>
                Select required test cases from the tree list
              </Typography>
              <Box className='treeListContainer'>
                <Box className='treeListSearchContainer'>
                  <Autocomplete
                    className='treeListSearch'
                    freeSolo
                    onInputChange={handleSearchInputChange}
                    options={generalTestCasesOptions}
                    getOptionLabel={(option) => formatTestCaseLabel(option)}
                    getOptionKey={option => option.id}
                    filterOptions={(options, { inputValue }) =>
                      options.filter((option) =>
                        testCaseMatchesSearch(option, inputValue)
                      )
                    }
                    value={searchText ? { name: searchText, id: 'temp-search-id' } : null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        className='treeListSearchInput'
                        variant="standard"
                        placeholder='Search...'
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: '#2D4356' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
                {renderTreeList()}
              </Box>
            </TabPanel>
          </TabContext>
          <Box className="addTestCaseModalSelectionSummary">
            <Typography variant="subtitle2" color="text.secondary">
              {selectedTestCaseDetails.length
                ? `Selected tests (${selectedTestCaseDetails.length}):`
                : "No tests selected yet."}
            </Typography>
            {selectedTestCaseDetails.length ? (
              <Box className="addTestCaseModalSelectionList">
                {previewSelectedTestCases.map((tc) => (
                  <Box
                    key={tc.id}
                    className="addTestCaseModalSelectionChip"
                    title={tc.name}
                  >
                    {formatTestCaseLabel(tc)}
                  </Box>
                ))}
                {hiddenSelectedCount > 0 ? (
                  <Box className="addTestCaseModalSelectionChip addTestCaseModalSelectionChip--muted">
                    +{hiddenSelectedCount} more
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions className="addTestCaseModalActions">
          <Button className="addTestCaseModalButton" onClick={handleCloseModal} color="secondary" variant="outlined">
            CANCEL
          </Button>
          {loading ? (
            <LoadingButton
              loading
              className="addTestCaseModalButton"
              variant="contained"
              loadingIndicator={<CircularProgress color="white" size={16} />}
            />
          ) : (
            <Button
              className="addTestCaseModalButton"
              onClick={handleAddTests}
              color="secondary"
              variant="contained"
              autoFocus
              disabled={selectedTestCases.length === 0}
            >
              CONFIRM
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export default AddTestsModal
